import { FormEvent, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useSaveCustomModelPrice } from "@/queries/customModelPrices";
import {
  CustomModelPricePayload,
  InputModalityTag,
  ModalityTag,
} from "@/queries/customModelPrices/types";

interface CustomModelPriceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialModelName?: string;
  initialPrices?: CustomModelPricePayload;
  lockModelName?: boolean;
}

type Modality = "text" | "audio" | "image";

const PriceInput = ({
  placeholder,
  value,
  onChange,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <Input
    type="number"
    min="0"
    step="0.001"
    className="flex-1"
    placeholder={placeholder}
    value={value}
    onChange={e => onChange(e.target.value)}
  />
);

const num = (value: number | undefined): string => (value === undefined ? "" : value.toString());

const buildTag = (text: string, audio: string, image: string): ModalityTag => {
  const tag: ModalityTag = {};
  if (text !== "") {
    tag.text_tokens = Number(text);
  }
  if (audio !== "") {
    tag.audio_tokens = Number(audio);
  }
  if (image !== "") {
    tag.image_tokens = Number(image);
  }
  return tag;
};

// Cache reads are priced on the input side only, so this wraps buildTag rather
// than widening it for both sides.
const buildInputTag = (
  text: string,
  audio: string,
  image: string,
  cacheRead: string,
): InputModalityTag => {
  const tag: InputModalityTag = buildTag(text, audio, image);
  if (cacheRead !== "") {
    tag.cache_read_tokens = Number(cacheRead);
  }
  return tag;
};

const CustomModelPriceForm = ({
  onOpenChange,
  initialModelName = "",
  initialPrices,
  lockModelName = false,
}: Omit<CustomModelPriceDialogProps, "open">) => {
  const [modelName, setModelName] = useState(initialModelName);
  const [cacheRead, setCacheRead] = useState(num(initialPrices?.input.cache_read_tokens));
  const [cacheInclusive, setCacheInclusive] = useState(
    initialPrices?.cache_tokens_inclusive ?? true,
  );
  const [inputText, setInputText] = useState(num(initialPrices?.input.text_tokens));
  const [outputText, setOutputText] = useState(num(initialPrices?.output.text_tokens));
  const [inputAudio, setInputAudio] = useState(num(initialPrices?.input.audio_tokens));
  const [outputAudio, setOutputAudio] = useState(num(initialPrices?.output.audio_tokens));
  const [inputImage, setInputImage] = useState(num(initialPrices?.input.image_tokens));
  const [outputImage, setOutputImage] = useState(num(initialPrices?.output.image_tokens));
  const [tab, setTab] = useState<Modality>("text");

  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId
    ? String(userContext.activeWorkspaceId)
    : undefined;
  const saveCustomPrice = useSaveCustomModelPrice(auth?.userToken ?? "", workspaceId);

  // Each side needs at least one modality priced; no single modality is
  // required — a model can be image-only or audio-only.
  const isDisabled = useMemo(() => {
    const inputHasRate = inputText !== "" || inputAudio !== "" || inputImage !== "";
    const outputHasRate = outputText !== "" || outputAudio !== "" || outputImage !== "";
    return saveCustomPrice.isLoading || modelName.trim() === "" || !inputHasRate || !outputHasRate;
  }, [
    saveCustomPrice.isLoading,
    modelName,
    inputText,
    inputAudio,
    inputImage,
    outputText,
    outputAudio,
    outputImage,
  ]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await saveCustomPrice.mutateAsync({
      model_name: modelName.trim(),
      input: buildInputTag(inputText, inputAudio, inputImage, cacheRead),
      output: buildTag(outputText, outputAudio, outputImage),
      cache_tokens_inclusive: cacheInclusive,
    });
    onOpenChange(false);
  };

  // The tab only selects which modality the single input row edits, so the row
  // stays in the same position regardless of the active tab.
  const active: Record<Modality, [string, (v: string) => void, string, (v: string) => void]> = {
    text: [inputText, setInputText, outputText, setOutputText],
    audio: [inputAudio, setInputAudio, outputAudio, setOutputAudio],
    image: [inputImage, setInputImage, outputImage, setOutputImage],
  };
  const [inputValue, setInputValue, outputValue, setOutputValue] = active[tab];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
      <Input
        placeholder="Model name (e.g. us.anthropic.claude-opus-4-7)"
        value={modelName}
        onChange={e => setModelName(e.target.value)}
        disabled={lockModelName}
      />
      <Tabs value={tab} onValueChange={value => setTab(value as Modality)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="text">Text</TabsTrigger>
          <TabsTrigger value="audio">Audio</TabsTrigger>
          <TabsTrigger value="image">Image</TabsTrigger>
        </TabsList>
      </Tabs>
      <div className="flex gap-x-3">
        <PriceInput placeholder="Input $ / 1M" value={inputValue} onChange={setInputValue} />
        <PriceInput placeholder="Output $ / 1M" value={outputValue} onChange={setOutputValue} />
      </div>
      {/* Cache reads sit outside the modality tabs: they're input-side only,
          with no output counterpart to pair against. */}
      <div className="flex flex-col gap-y-2 border-t pt-4">
        <PriceInput
          placeholder="Cache read $ / 1M (optional)"
          value={cacheRead}
          onChange={setCacheRead}
        />
        <Checkbox
          checked={cacheInclusive}
          onChange={e => setCacheInclusive(e.target.checked)}
          label="Provider counts cached tokens inside input tokens"
        />
        <p className="text-xs text-muted-foreground">
          Leave checked for OpenAI-compatible APIs. Uncheck for Anthropic-style APIs that report
          cached tokens separately from input tokens.
        </p>
      </div>
      <Button type="submit" disabled={isDisabled}>
        Save
      </Button>
    </form>
  );
};

export const CustomModelPriceDialog = ({
  open,
  onOpenChange,
  ...formProps
}: CustomModelPriceDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Custom model price</DialogTitle>
        <DialogDescription>
          Set a price for a model by its exact name. Prices are per 1M tokens and apply to logged
          requests that use that model.
        </DialogDescription>
      </DialogHeader>
      {/* Remount per open so the form resets to the provided initial values. */}
      {open && (
        <CustomModelPriceForm
          key={formProps.initialModelName ?? ""}
          onOpenChange={onOpenChange}
          {...formProps}
        />
      )}
    </DialogContent>
  </Dialog>
);
